import { isoToUtcAnchor } from './dates';
import type { DayDensity, Density, DensityClass, Respondent } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;

export function buildDensity(respondents: Respondent[]): Density {
  const out: Density = {};
  for (const r of respondents) {
    for (const day of r.yes) {
      const d = out[day] ?? { yes: 0, maybe: 0 };
      d.yes += 1;
      out[day] = d;
    }
    for (const day of r.maybe) {
      const d = out[day] ?? { yes: 0, maybe: 0 };
      d.maybe += 1;
      out[day] = d;
    }
  }
  return out;
}

export interface OverlapEntry {
  day: string;
  yes: number;
  maybe: number;
  weighted: number;
}

function weight(d: DayDensity): number {
  return d.yes + d.maybe * 0.5;
}

export function topOverlap(density: Density, k = 10): OverlapEntry[] {
  const entries: OverlapEntry[] = [];
  for (const [day, d] of Object.entries(density)) {
    if (d.yes + d.maybe === 0) continue;
    entries.push({ day, yes: d.yes, maybe: d.maybe, weighted: weight(d) });
  }
  entries.sort((a, b) => {
    if (b.weighted !== a.weighted) return b.weighted - a.weighted;
    if (b.yes !== a.yes) return b.yes - a.yes;
    if (a.day < b.day) return -1;
    if (a.day > b.day) return 1;
    return 0;
  });
  return entries.slice(0, k);
}

export function densityClass(d: DayDensity, total: number): DensityClass {
  if (total <= 0) return 0;
  const w = weight(d);
  if (w <= 0) return 0;
  const pct = w / total;
  if (pct >= 0.999) return 5;
  if (pct >= 0.75) return 4;
  if (pct >= 0.5) return 3;
  if (pct >= 0.25) return 2;
  return 1;
}

export interface DayRun {
  start: string;
  end: string;
  days: string[];
}

export function everyoneFreeRuns(density: Density, total: number): DayRun[] {
  if (total <= 0) return [];
  const all = Object.entries(density)
    .filter(([, d]) => d.yes === total)
    .map(([day]) => day)
    .sort();
  if (all.length === 0) return [];

  const runs: DayRun[] = [];
  let current: string[] = [all[0]!];

  for (let i = 1; i < all.length; i += 1) {
    const prev = all[i - 1]!;
    const curr = all[i]!;
    const gap = isoToUtcAnchor(curr).getTime() - isoToUtcAnchor(prev).getTime();
    if (gap === DAY_MS) {
      current.push(curr);
    } else {
      runs.push({ start: current[0]!, end: current[current.length - 1]!, days: current });
      current = [curr];
    }
  }
  runs.push({ start: current[0]!, end: current[current.length - 1]!, days: current });
  return runs;
}
